import { db } from '../config/firebase';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

export interface OTPResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Generate an activation token
 */
export const generateActivationToken = (): string => {
  // Generate a secure random token
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

/**
 * Mailjet API configuration
 * Note: For Spark plan, we call Mailjet API directly from frontend
 * API keys are stored in environment variables
 */
const MAILJET_API_KEY = process.env.REACT_APP_MAILJET_API_KEY || '';
const MAILJET_API_SECRET = process.env.REACT_APP_MAILJET_API_SECRET || '';
const MAILJET_FROM_EMAIL = process.env.REACT_APP_MAILJET_FROM_EMAIL || 'sales@joyinfosolutions.com';
const MAILJET_FROM_NAME = process.env.REACT_APP_MAILJET_FROM_NAME || 'ManjuShree Foundation';

// Use local proxy server to bypass CORS (run proxy-server.js)
const PROXY_SERVER = process.env.REACT_APP_PROXY_SERVER || 'http://localhost:3001';
const MAILJET_API_URL = 'https://api.mailjet.com/v3.1/send';
const PROXY_API_URL = `${PROXY_SERVER}/api/send-email`;

/**
 * Send activation email via Mailjet REST API (Spark plan compatible)
 */
const sendEmailViaMailjet = async (toEmail: string, activationToken: string): Promise<boolean> => {
  // Debug: Log configuration status
  console.log('🔧 Mailjet Configuration Check:');
  console.log('  API Key:', MAILJET_API_KEY ? `${MAILJET_API_KEY.substring(0, 10)}...` : '❌ MISSING');
  console.log('  API Secret:', MAILJET_API_SECRET ? '✅ Set' : '❌ MISSING');
  console.log('  From Email:', MAILJET_FROM_EMAIL);
  console.log('  From Name:', MAILJET_FROM_NAME);
  
  if (!MAILJET_API_KEY || !MAILJET_API_SECRET) {
    console.error('❌ Mailjet credentials not configured!');
    console.error('Please set REACT_APP_MAILJET_API_KEY and REACT_APP_MAILJET_API_SECRET in .env file');
    console.error('Then restart your dev server (npm start)');
    return false;
  }

  // Generate activation link
  const activationLink = `${window.location.origin}/activate?token=${activationToken}&email=${encodeURIComponent(toEmail)}`;
  
  const subject = 'Activate Your Account - Manjushree Foundation';
  const htmlBody = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #2c3e50;">Hello,</h2>
          <p>Thank you for your interest in supporting Manjushree Foundation!</p>
          <p>Please activate your account by clicking the button below:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="${activationLink}" style="display: inline-block; padding: 15px 30px; background-color: #f4c430; color: #fff; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 16px;">Activate Account</a>
          </div>
          <p style="color: #666; font-size: 14px;">Or copy and paste this link into your browser:</p>
          <p style="color: #2c3e50; font-size: 12px; word-break: break-all; background-color: #f4f4f4; padding: 10px; border-radius: 5px;">${activationLink}</p>
          <p style="color: #e74c3c; font-weight: bold; font-size: 14px;">This activation link will expire in 24 hours.</p>
          <p style="color: #666; font-size: 14px;">If you didn't create an account, please ignore this email.</p>
          <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">
          <p style="color: #7f8c8d; font-size: 12px;">Best regards,<br>${MAILJET_FROM_NAME}</p>
        </div>
      </body>
    </html>
  `;

  const textBody = `
Hello,

Thank you for your interest in supporting Manjushree Foundation!

Please activate your account by clicking the link below:

${activationLink}

This activation link will expire in 24 hours.

If you didn't create an account, please ignore this email.

Best regards,
${MAILJET_FROM_NAME}
  `.trim();

  try {
    console.log('\n  [3.1] Preparing API Request...');
    
    // Create Basic Auth header
    const credentials = btoa(`${MAILJET_API_KEY}:${MAILJET_API_SECRET}`);
    console.log('  ✅ Basic Auth credentials created');
    
    // Prepare request payload
    const requestPayload = {
      Messages: [
        {
          From: {
            Email: MAILJET_FROM_EMAIL,
            Name: MAILJET_FROM_NAME,
          },
          To: [
            {
              Email: toEmail,
            },
          ],
          Subject: subject,
          TextPart: textBody,
          HTMLPart: htmlBody,
        },
      ],
    };
    
    console.log('  📋 Request Payload:');
    console.log('    API URL:', MAILJET_API_URL);
    console.log('    From:', `${MAILJET_FROM_NAME} <${MAILJET_FROM_EMAIL}>`);
    console.log('    To:', toEmail);
    console.log('    Subject:', subject);
    console.log('    Activation Token:', activationToken.substring(0, 8) + '...');
    
    console.log('\n  [3.2] Sending HTTP Request to Mailjet...');
    const requestStartTime = Date.now();
    
    // Use local proxy server to bypass CORS restrictions
    // The proxy server (proxy-server.js) must be running on localhost:3001
    console.log('  🔄 Using local CORS proxy server');
    console.log('  📍 Proxy URL:', PROXY_API_URL);
    console.log('  📍 Target API:', MAILJET_API_URL);
    console.log('  ⚠️  Make sure proxy-server.js is running!');
    
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${credentials}`,
      },
      body: JSON.stringify(requestPayload),
    };
    
    console.log('  📤 Making fetch request through local proxy...');
    const response = await fetch(PROXY_API_URL, fetchOptions);
    
    const requestDuration = Date.now() - requestStartTime;
    console.log(`  ⏱️  Request completed in ${requestDuration}ms`);
    console.log('  📡 Response Status:', response.status, response.statusText);
    console.log('  📡 Response OK:', response.ok);

    console.log('\n  [3.3] Processing Response...');
    
    if (!response.ok) {
      console.error('  ❌ HTTP Error Response Received');
      console.error('  Status Code:', response.status);
      console.error('  Status Text:', response.statusText);
      
      // Try to parse error response
      let errorData = {};
      try {
        const errorText = await response.text();
        console.log('  📄 Raw Error Response:', errorText);
        errorData = JSON.parse(errorText);
        console.log('  📋 Parsed Error Data:', errorData);
      } catch (parseError) {
        console.warn('  ⚠️  Could not parse error response as JSON');
        console.warn('  Parse Error:', parseError);
      }
      
      // Provide helpful error messages
      console.error('\n  🔍 Error Analysis:');
      if (response.status === 401) {
        console.error('  ❌ 401 Unauthorized: Authentication failed');
        console.error('  💡 Solution: Check API Key and Secret in .env file');
        console.error('  💡 Verify credentials match Mailjet dashboard');
      } else if (response.status === 400) {
        console.error('  ❌ 400 Bad Request: Invalid request parameters');
        console.error('  💡 Solution: Verify sender email is verified in Mailjet dashboard');
        console.error('  💡 Check: Senders & Domains → Senders → Verify email');
      } else if (response.status === 403) {
        console.error('  ❌ 403 Forbidden: Access denied');
        console.error('  💡 Solution: Check API key permissions in Mailjet');
        console.error('  💡 Verify account is active and not suspended');
      } else if (response.status === 429) {
        console.error('  ❌ 429 Too Many Requests: Rate limit exceeded');
        console.error('  💡 Solution: Wait a few minutes and try again');
        console.error('  💡 Check Mailjet dashboard for usage limits');
      } else {
        console.error(`  ❌ ${response.status} ${response.statusText}: Unexpected error`);
        console.error('  💡 Check Mailjet dashboard for account status');
      }
      
      return false;
    }

    // Parse successful response
    console.log('  ✅ HTTP Response OK, parsing JSON...');
    let result;
    try {
      const responseText = await response.text();
      console.log('  📄 Raw Response:', responseText.substring(0, 200) + '...');
      result = JSON.parse(responseText);
      console.log('  📋 Parsed Response:', JSON.stringify(result, null, 2));
    } catch (parseError) {
      console.error('  ❌ Failed to parse response JSON');
      console.error('  Parse Error:', parseError);
      return false;
    }
    
    // Validate response structure
    console.log('\n  [3.4] Validating Response...');
    if (result.Messages && result.Messages[0]) {
      const message = result.Messages[0];
      console.log('  📬 Message Status:', message.Status);
      console.log('  📬 Message Details:', message);
      
      if (message.Status === 'success') {
        const messageId = message.To?.[0]?.MessageID || 'N/A';
        console.log('  ✅ Email sent successfully via Mailjet');
        console.log('  📧 Message ID:', messageId);
        console.log('  📧 Recipient:', message.To?.[0]?.Email || toEmail);
        return true;
      } else {
        console.warn('  ⚠️  Message status is not "success"');
        console.warn('  Status:', message.Status);
        console.warn('  Errors:', message.Errors || 'No error details');
      }
    } else {
      console.warn('  ⚠️  Unexpected response structure');
      console.warn('  Response:', result);
    }

    return false;
  } catch (error: any) {
    console.error('Error sending email via Mailjet:', error);
    return false;
  }
};

/**
 * Send activation email using Mailjet API (Spark plan compatible)
 * Note: This calls Mailjet directly from the frontend
 */
export const sendOTPToEmail = async (email: string): Promise<OTPResponse> => {
  console.log('═══════════════════════════════════════════════════════════');
  console.log('🚀 STARTING ACCOUNT ACTIVATION EMAIL PROCESS');
  console.log('═══════════════════════════════════════════════════════════');
  console.log('📧 Recipient Email:', email);
  console.log('⏰ Timestamp:', new Date().toISOString());
  
  try {
    // Step 1: Generate activation token
    console.log('\n[STEP 1] Generating activation token...');
    const activationToken = generateActivationToken();
    const expiresAt = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
    console.log('✅ Activation Token Generated:', activationToken.substring(0, 8) + '...');
    console.log('⏳ Expires At:', new Date(expiresAt).toISOString());
    console.log('⏱️  Valid for: 24 hours');

    // Step 2: Store OTP in Firestore (non-blocking)
    console.log('\n[STEP 2] Storing OTP in Firestore...');
    let firestoreSuccess = false;
    
    // Use Promise.race to add timeout and prevent hanging
    const firestorePromise = (async () => {
      try {
        console.log('  📍 Collection: otps');
        console.log('  📍 Document ID:', email);
        const otpRef = doc(db, 'otps', email);
        
        console.log('  💾 Writing to Firestore...');
      await setDoc(otpRef, {
        activationToken: activationToken,
        expiresAt,
        createdAt: serverTimestamp(),
        email,
        activated: false,
      });
        firestoreSuccess = true;
        console.log('✅ OTP stored in Firestore successfully');
      } catch (firestoreError: any) {
        console.error('❌ Firestore Error:');
        console.error('  Error Code:', firestoreError.code);
        console.error('  Error Message:', firestoreError.message);
        console.error('  Full Error:', firestoreError);
        console.warn('⚠️  Firestore not available. OTP will be logged to console.');
        console.warn('⚠️  Continuing with email sending...');
      }
    })();
    
    // Add timeout to prevent hanging (5 seconds max)
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        console.warn('⏱️  Firestore operation taking too long, continuing with email...');
        resolve(null);
      }, 5000);
    });
    
    // Wait for Firestore with timeout
    await Promise.race([firestorePromise, timeoutPromise]);
    
    console.log('\n[STEP 2 COMPLETE] Firestore operation finished (or timed out)');
    console.log('  Firestore Status:', firestoreSuccess ? '✅ Success' : '⚠️  Failed or Timeout');

    // Step 3: Send email using Mailjet API
    console.log('\n[STEP 3] Sending email via Mailjet API...');
    console.log('  📤 Starting email send process...');
    let emailSent = false;
    
    try {
      console.log('  📤 Initiating Mailjet API call...');
      emailSent = await sendEmailViaMailjet(email, activationToken);
      
      if (emailSent) {
        console.log('✅ Activation email sent successfully via Mailjet');
      } else {
        console.warn('⚠️  Mailjet API returned unsuccessful result');
        console.warn('⚠️  Check logs above for detailed error information');
      }
    } catch (emailError: any) {
      console.error('❌ Mailjet API Exception:');
      console.error('  Error Type:', emailError.name);
      console.error('  Error Message:', emailError.message);
      console.error('  Error Stack:', emailError.stack);
      console.error('  Full Error Object:', emailError);
      
      // Check for specific error types
      if (emailError.name === 'TypeError') {
        console.error('  🔍 This might be a network/CORS issue');
      } else if (emailError.name === 'SyntaxError') {
        console.error('  🔍 This might be a JSON parsing issue');
      }
      
      // Fallback: Log activation link to console for development/testing
      const activationLink = `${window.location.origin}/activate?token=${activationToken}&email=${encodeURIComponent(email)}`;
      console.log('\n═══════════════════════════════════════');
      console.log('📧 ACCOUNT ACTIVATION EMAIL (FALLBACK)');
      console.log('═══════════════════════════════════════');
      console.log('Email:', email);
      console.log('Activation Link:', activationLink);
      console.log('Expires in: 24 hours');
      console.log('═══════════════════════════════════════');
      console.warn('Mailjet API failed. Activation link logged above. Check Mailjet configuration.');
    }

    // Step 4: Summary and Fallback
    console.log('\n[STEP 4] Process Summary:');
    console.log('  📊 Firestore Status:', firestoreSuccess ? '✅ Success' : '❌ Failed');
    console.log('  📧 Email Status:', emailSent ? '✅ Success' : '❌ Failed');
    
    // Fallback: Always log activation link to console for development/testing if email wasn't sent
    if (!emailSent) {
      const activationLink = `${window.location.origin}/activate?token=${activationToken}&email=${encodeURIComponent(email)}`;
      console.log('\n═══════════════════════════════════════');
      console.log('📧 ACCOUNT ACTIVATION EMAIL (FALLBACK)');
      console.log('═══════════════════════════════════════');
      console.log('📧 Email:', email);
      console.log('🔗 Activation Link:', activationLink);
      console.log('⏱️  Expires in: 24 hours');
      console.log('⏰ Expires at:', new Date(expiresAt).toISOString());
      console.log('═══════════════════════════════════════');
      console.warn('⚠️  Mailjet not configured or failed. Activation link logged above.');
      console.warn('⚠️  Use this OTP code for testing.');
    }

    // Final Result
    console.log('\n═══════════════════════════════════════════════════════════');
    if (firestoreSuccess || emailSent) {
      console.log('✅ OTP PROCESS COMPLETED');
      console.log('═══════════════════════════════════════════════════════════');
      return { 
        success: true, 
        message: emailSent 
          ? 'Activation email sent successfully! Please check your inbox.' 
          : 'Activation link generated. Check console for activation link.' 
      };
    } else {
      console.log('❌ OTP PROCESS FAILED');
      console.log('═══════════════════════════════════════════════════════════');
      return { 
        success: false, 
        error: 'Failed to send activation email. Please check console for activation link and error details.' 
      };
    }
  } catch (error: any) {
    console.error('\n❌❌❌ UNEXPECTED ERROR IN OTP PROCESS ❌❌❌');
    console.error('Error Type:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Full Error Object:', error);
    console.error('═══════════════════════════════════════════════════════════');
    
    return {
      success: false,
      error: error.message || 'Failed to send OTP. Please try again.'
    };
  }
};

/**
 * Activate account using activation token from email link
 */
export const activateAccount = async (email: string, token: string): Promise<OTPResponse> => {
  console.log('═══════════════════════════════════════════════════════════');
  console.log('🔐 STARTING ACCOUNT ACTIVATION');
  console.log('═══════════════════════════════════════════════════════════');
  console.log('📧 Email:', email);
  console.log('🔑 Activation Token:', token.substring(0, 8) + '...');
  console.log('⏰ Timestamp:', new Date().toISOString());
  
  try {
    console.log('\n[STEP 1] Fetching activation token from Firestore...');
    console.log('  📍 Collection: otps');
    console.log('  📍 Document ID:', email);
    
    try {
      const otpRef = doc(db, 'otps', email);
      console.log('  📥 Reading document...');
      const otpDoc = await getDoc(otpRef);

      if (otpDoc.exists()) {
        console.log('  ✅ Activation document found');
        const activationData = otpDoc.data();
        console.log('  📋 Activation Data:', {
          email: activationData.email,
          activated: activationData.activated || false,
          expiresAt: new Date(activationData.expiresAt).toISOString(),
        });
        
        const now = Date.now();
        const expiresAt = activationData.expiresAt;
        const timeRemaining = Math.floor((expiresAt - now) / 1000 / 60);
        
        console.log('\n[STEP 2] Validating activation token...');
        console.log('  ⏰ Current Time:', new Date(now).toISOString());
        console.log('  ⏰ Expires At:', new Date(expiresAt).toISOString());
        console.log('  ⏱️  Time Remaining:', timeRemaining > 0 ? `${timeRemaining} minutes` : 'EXPIRED');

        // Check if already activated
        if (activationData.activated === true) {
          console.log('  ⚠️  Account already activated');
          console.log('  ✅ Account is already active');
          
          console.log('\n═══════════════════════════════════════════════════════════');
          console.log('✅ ACCOUNT ALREADY ACTIVATED');
          console.log('═══════════════════════════════════════════════════════════');
          return { success: true, message: 'Account is already activated.' };
        }

        // Check if token has expired
        if (expiresAt < now) {
          console.log('  ❌ Activation token has expired');
          console.log('  🗑️  Deleting expired token...');
          await deleteDoc(otpRef);
          console.log('  ✅ Expired token deleted');
          return { success: false, error: 'Activation link has expired. Please request a new activation email.' };
        }

        console.log('  ✅ Token is still valid');
        console.log('  🔍 Comparing tokens...');
        const storedToken = activationData.activationToken;
        console.log('    Stored Token:', storedToken ? storedToken.substring(0, 8) + '...' : 'N/A');
        console.log('    Provided Token:', token.substring(0, 8) + '...');

        // Verify token
        if (storedToken === token) {
          console.log('  ✅ Token matches!');
          console.log('  🔄 Activating account...');
          
          // Mark as activated instead of deleting
          await setDoc(otpRef, {
            ...activationData,
            activated: true,
            activatedAt: serverTimestamp(),
          }, { merge: true });
          
          console.log('  ✅ Account activated successfully');
          
          console.log('\n═══════════════════════════════════════════════════════════');
          console.log('✅ ACCOUNT ACTIVATION SUCCESSFUL');
          console.log('═══════════════════════════════════════════════════════════');
          return { success: true, message: 'Account activated successfully!' };
        } else {
          console.log('  ❌ Token does not match');
          console.log('  ⚠️  Invalid activation token');
          
          console.log('\n═══════════════════════════════════════════════════════════');
          console.log('❌ ACCOUNT ACTIVATION FAILED - Invalid Token');
          console.log('═══════════════════════════════════════════════════════════');
          return { success: false, error: 'Invalid activation link. Please request a new activation email.' };
        }
      } else {
        console.log('  ❌ Activation document not found');
        console.log('  💡 Token may have been used already or never created');
      }
    } catch (firestoreError: any) {
      console.error('\n❌ Firestore Error During Activation:');
      console.error('  Error Code:', firestoreError.code);
      console.error('  Error Message:', firestoreError.message);
      console.error('  Full Error:', firestoreError);
      
      // Check for specific error types
      if (firestoreError.code === 'unavailable' || firestoreError.message?.includes('offline')) {
        console.error('  🔍 Firestore is offline or blocked');
        console.error('  💡 Possible causes:');
        console.error('     - Ad blocker blocking Firestore');
        console.error('     - Browser extension blocking requests');
        console.error('     - Network/firewall blocking firestore.googleapis.com');
        console.error('  💡 Solutions:');
        console.error('     - Disable ad blocker for this site');
        console.error('     - Check browser extensions');
        console.error('     - Check network/firewall settings');
        console.error('     - Try different browser or incognito mode');
      }
      
      console.warn('  ⚠️  Firestore activation failed');
    }

    // If Firestore doesn't have the token, return error
    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('❌ ACCOUNT ACTIVATION FAILED - Token Not Found');
    console.log('═══════════════════════════════════════════════════════════');
    return { success: false, error: 'Activation link not found or already used. Please request a new activation email.' };
  } catch (error: any) {
    console.error('\n❌❌❌ UNEXPECTED ERROR IN ACCOUNT ACTIVATION ❌❌❌');
    console.error('Error Type:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Full Error Object:', error);
    console.error('═══════════════════════════════════════════════════════════');
    
    return {
      success: false,
      error: error.message || 'Failed to activate account. Please try again.'
    };
  }
};

/**
 * Verify OTP entered by user from Firestore
 */
export const verifyOTPCode = async (email: string, otp: string): Promise<OTPResponse> => {
  console.log('═══════════════════════════════════════════════════════════');
  console.log('🔐 STARTING OTP VERIFICATION');
  console.log('═══════════════════════════════════════════════════════════');
  console.log('📧 Email:', email);
  console.log('🔢 OTP Entered:', otp);
  console.log('⏰ Timestamp:', new Date().toISOString());
  
  try {
    console.log('\n[STEP 1] Fetching OTP from Firestore...');
    console.log('  📍 Collection: otps');
    console.log('  📍 Document ID:', email);
    
    // Try to verify from Firestore
    try {
      const otpRef = doc(db, 'otps', email);
      console.log('  📥 Reading document...');
      const otpDoc = await getDoc(otpRef);

      if (otpDoc.exists()) {
        console.log('  ✅ OTP document found');
        const otpData = otpDoc.data();
        console.log('  📋 OTP Data:', {
          email: otpData.email,
          otp: otpData.otp ? '***' + otpData.otp.slice(-2) : 'N/A',
          expiresAt: new Date(otpData.expiresAt).toISOString(),
        });
        
        const now = Date.now();
        const expiresAt = otpData.expiresAt;
        const timeRemaining = Math.floor((expiresAt - now) / 1000 / 60);
        
        console.log('\n[STEP 2] Validating OTP...');
        console.log('  ⏰ Current Time:', new Date(now).toISOString());
        console.log('  ⏰ Expires At:', new Date(expiresAt).toISOString());
        console.log('  ⏱️  Time Remaining:', timeRemaining > 0 ? `${timeRemaining} minutes` : 'EXPIRED');

        // Check if OTP has expired
        if (expiresAt < now) {
          console.log('  ❌ OTP has expired');
          console.log('  🗑️  Deleting expired OTP...');
          await deleteDoc(otpRef);
          console.log('  ✅ Expired OTP deleted');
          return { success: false, error: 'OTP has expired. Please request a new OTP.' };
        }

        console.log('  ✅ OTP is still valid');
        console.log('  🔍 Comparing OTPs...');
        console.log('    Stored OTP:', otpData.otp ? '***' + otpData.otp.slice(-2) : 'N/A');
        console.log('    Entered OTP:', otp);

        // Verify OTP
        if (otpData.otp === otp) {
          console.log('  ✅ OTP matches!');
          console.log('  🗑️  Deleting verified OTP...');
          await deleteDoc(otpRef);
          console.log('  ✅ OTP deleted after successful verification');
          
          console.log('\n═══════════════════════════════════════════════════════════');
          console.log('✅ OTP VERIFICATION SUCCESSFUL');
          console.log('═══════════════════════════════════════════════════════════');
          return { success: true, message: 'OTP verified successfully' };
        } else {
          console.log('  ❌ OTP does not match');
          console.log('  ⚠️  Invalid OTP entered');
          
          console.log('\n═══════════════════════════════════════════════════════════');
          console.log('❌ OTP VERIFICATION FAILED - Invalid OTP');
          console.log('═══════════════════════════════════════════════════════════');
          return { success: false, error: 'Invalid OTP. Please try again.' };
        }
      } else {
        console.log('  ❌ OTP document not found');
        console.log('  💡 OTP may have been used already or never created');
      }
    } catch (firestoreError: any) {
      console.error('\n❌ Firestore Error During Verification:');
      console.error('  Error Code:', firestoreError.code);
      console.error('  Error Message:', firestoreError.message);
      console.error('  Full Error:', firestoreError);
      console.warn('  ⚠️  Firestore verification failed');
    }

    // If Firestore doesn't have the OTP, return error
    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('❌ OTP VERIFICATION FAILED - OTP Not Found');
    console.log('═══════════════════════════════════════════════════════════');
    return { success: false, error: 'OTP not found. Please request a new OTP.' };
  } catch (error: any) {
    console.error('\n❌❌❌ UNEXPECTED ERROR IN OTP VERIFICATION ❌❌❌');
    console.error('Error Type:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Full Error Object:', error);
    console.error('═══════════════════════════════════════════════════════════');
    
    return {
      success: false,
      error: error.message || 'Invalid OTP. Please try again.'
    };
  }
};

